import { useEffect, useState, useRef } from "react";
import { useSettings } from "@/context/SettingsContext";
import api from "@/utils/api";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  CreditCard,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  User,
  Printer,
  Edit2,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import MemberPrintProfile from "@/components/MemberPrintProfile";
import ReceiptPrint from "@/components/ReceiptPrint";
import { printReceipt, downloadReceiptPDF, shareReceiptPDF, numberToWords } from "@/utils/printUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MemberDetail = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, formatDate, settings } = useSettings();
  const profileRef = useRef();
  const receiptRef = useRef();
  const [member, setMember] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReceiptEditOpen, setIsReceiptEditOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [receiptEditForm, setReceiptEditForm] = useState({ amount: '', payment_method: 'cash', description: '' });
  const [printAdmissionReceipt, setPrintAdmissionReceipt] = useState(null);
  const [showAdmissionPrintDialog, setShowAdmissionPrintDialog] = useState(false);

  const handlePrintProfile = () => {
    if (profileRef.current) {
      printReceipt(profileRef.current, 'a4');
    }
  };

  const handlePrintAdmissionReceipt = () => {
    if (!member?.admission_fee_paid) {
      toast.error('Admission fee not paid yet');
      return;
    }
    const user = JSON.parse(localStorage.getItem('user'));
    const company = user?.company;
    const receiptData = {
      receipt_number: `ADM-${member.id.slice(0, 8).toUpperCase()}`,
      receipt_date: member.admission_fee_payment_date || member.join_date,
      receipt_type: 'admission_fee',
      amount: member.admission_fee_amount,
      payment_method: 'Cash',
      description: 'Admission Fee',
      member_name: member.name,
      member_email: member.email,
      member_phone: member.phone,
      company_name: company?.name,
      company_address: company?.address,
      company_phone: company?.phone,
      company_email: company?.email,
      company_logo_url: company?.logo_url,
      amount_in_words: numberToWords(member.admission_fee_amount || 0)
    };
    setPrintAdmissionReceipt(receiptData);
    setShowAdmissionPrintDialog(true);
  };

  const handleAdmissionPrint = (format) => {
    if (receiptRef.current) {
      printReceipt(receiptRef.current, format);
    }
  };

  const handleAdmissionDownloadPDF = async (format) => {
    if (receiptRef.current && printAdmissionReceipt) {
      await downloadReceiptPDF(
        receiptRef.current,
        `admission-receipt-${printAdmissionReceipt.receipt_number}.pdf`,
        format
      );
    }
  };

  const handleAdmissionSharePDF = async (format) => {
    if (receiptRef.current && printAdmissionReceipt) {
      await shareReceiptPDF(
        receiptRef.current,
        `admission-receipt-${printAdmissionReceipt.receipt_number}.pdf`,
        format
      );
    }
  };

  const openReceiptEdit = (receipt) => {
    setEditingReceipt(receipt);
    setReceiptEditForm({
      amount: String(receipt.amount || ''),
      payment_method: receipt.payment_method || 'cash',
      description: receipt.description || ''
    });
    setIsReceiptEditOpen(true);
  };

  const submitReceiptEdit = async (e) => {
    e.preventDefault();
    if (!editingReceipt) return;
    try {
      const payload = {
        amount: parseFloat(receiptEditForm.amount),
        payment_method: receiptEditForm.payment_method,
        description: receiptEditForm.description
      };
      await api.patch(`/receipts/${editingReceipt.id}`, payload);
      toast.success('Receipt updated');
      setIsReceiptEditOpen(false);
      setEditingReceipt(null);
      const res = await api.get(`/members/${memberId}/receipts`);
      setPayments(res.data);
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update receipt');
    }
  };

  const deleteReceipt = async (receipt) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await api.delete(`/receipts/${receipt.id}`);
      toast.success('Receipt deleted');
      const res = await api.get(`/members/${memberId}/receipts`);
      setPayments(res.data);
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  useEffect(() => {
    fetchMemberData();
    const handler = () => fetchMemberData();
    window.addEventListener('ledgerUpdated', handler);
    return () => window.removeEventListener('ledgerUpdated', handler);
  }, [memberId]);

  const fetchMemberData = async () => {
    try {
      const [memberRes, subsRes, attendanceRes, paymentsRes, plansRes] = await Promise.all([
        api.get(`/members/${memberId}`),
        api.get(`/memberships/member/${memberId}`),
        api.get(`/attendance/member/${memberId}`),
        api.get(`/members/${memberId}/receipts`),
        api.get(`/membership-plans`),
      ]);
      setMember(memberRes.data);
      setSubscriptions(subsRes.data);
      setAttendance(attendanceRes.data);
      setPayments(paymentsRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error("Failed to load member data");
      navigate("/members");
    } finally {
      setLoading(false);
    }
  };

  const getPlanName = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    return plan ? plan.name : "Unknown Plan";
  };

  const getSubscriptionStatus = (endDate, status) => {
    if (status !== "active") {
      return {
        label: "Inactive",
        variant: "secondary",
        icon: AlertCircle,
        color: "text-zinc-500"
      };
    }

    const end = new Date(endDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return {
        label: "Expired",
        variant: "destructive",
        icon: AlertCircle,
        color: "text-red-500"
      };
    } else if (daysUntilExpiry <= (settings.membershipDueReminder || 7)) {
      return {
        label: `Expires in ${daysUntilExpiry} days`,
        variant: "warning",
        icon: AlertCircle,
        color: "text-orange-500"
      };
    } else {
      return {
        label: `Active (${daysUntilExpiry} days left)`,
        variant: "success",
        icon: CheckCircle,
        color: "text-lime-600"
      };
    }
  };

  const calculateStats = () => {
    const totalSpent = payments.reduce((sum, p) => sum + p.amount, 0);
    const totalAttendance = attendance.length;
    const activeSubscription = subscriptions.find(s => {
      const end = new Date(s.end_date);
      return s.status === 'active' && end > new Date();
    });

    // Get current month attendance
    const currentMonth = new Date().toISOString().slice(0, 7);
    const monthlyAttendance = attendance.filter(a => a.date.startsWith(currentMonth)).length;

    return {
      totalSpent,
      totalAttendance,
      monthlyAttendance,
      hasActiveSubscription: !!activeSubscription
    };
  };

  const calculatePendingPayments = () => {
    const pendingItems = [];

    // Pending Admission Fee
    if (!member.admission_fee_paid && member.admission_fee_amount > 0) {
      pendingItems.push({
        id: 'admission',
        type: 'Admission Fee',
        description: 'One-time admission fee',
        amount: member.admission_fee_amount,
        date: member.join_date,
        isAdmission: true
      });
    }

    // Pending Subscriptions
    subscriptions.forEach(sub => {
      const pendingAmount = (sub.amount_due || 0) - (sub.amount_paid || 0);
      if ((sub.payment_status === 'pending' || sub.payment_status === 'partial') && pendingAmount > 0) {
        pendingItems.push({
          id: sub.id,
          type: 'Subscription',
          description: getPlanName(sub.plan_id),
          amount: pendingAmount,
          date: sub.start_date,
          isSubscription: true
        });
      }
    });

    return pendingItems;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  if (!member) {
    return null;
  }

  const stats = calculateStats();
  const pendingPayments = calculatePendingPayments();

  return (
    <div data-testid="member-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/members")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Members</span>
        </button>
        
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h1 className="text-4xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {member.name}
                </h1>
                <Button
                  onClick={handlePrintProfile}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                >
                  <Printer size={16} className="mr-2" />
                  Print Profile
                </Button>
                {member.admission_fee_paid && (
                  <Button
                    onClick={handlePrintAdmissionReceipt}
                    className="ml-2 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <Printer size={16} className="mr-2" />
                    Print Admission Receipt
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-zinc-600">
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span>{member.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>{member.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield size={16} />
                  <span>Emergency: {member.emergency_contact}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span>Joined: {formatDate(member.join_date)}</span>
                </div>
                {member.gender && (
                  <div className="flex items-center gap-2">
                    <User size={16} />
                    <span className="capitalize">{member.gender}</span>
                  </div>
                )}
                {member.date_of_birth && (
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span>DOB: {formatDate(member.date_of_birth)}</span>
                  </div>
                )}
                {(member.height || member.weight) && (
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} />
                    <span>
                      {member.height && `${member.height}cm`}
                      {member.height && member.weight && ' • '}
                      {member.weight && `${member.weight}kg`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="text-right">
              <Badge className={`${
                member.status === 'active' 
                  ? 'bg-lime-100 text-lime-800 hover:bg-lime-200' 
                  : 'bg-zinc-100 text-zinc-800'
              }`}>
                {member.status.toUpperCase()}
              </Badge>
              {stats.hasActiveSubscription && (
                <div className="mt-2 text-sm text-lime-600 font-medium">
                  ✓ Active Membership
                </div>
              )}
              {member.admission_fee_amount && (
                <div className={`mt-2 px-3 py-1 rounded text-xs font-medium ${
                  member.admission_fee_paid
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  Admission Fee: {formatCurrency(member.admission_fee_amount)} 
                  {member.admission_fee_paid ? ' ✓' : ' (Pending)'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pending Payments Alert */}
      {pendingPayments.length > 0 && (
        <div className="mb-8 bg-orange-50 border border-orange-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-orange-200 bg-orange-100/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-orange-600" size={20} />
              <h2 className="text-xl font-bold text-orange-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Pending Payments
              </h2>
            </div>
            <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
              {pendingPayments.length} Pending
            </Badge>
          </div>
          <div className="divide-y divide-orange-200/50">
            {pendingPayments.map((payment) => (
              <div key={payment.id} className="px-6 py-4 flex items-center justify-between hover:bg-orange-100/30 transition-colors">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-zinc-900">{payment.type}</span>
                    <span className="text-zinc-500">•</span>
                    <span className="text-zinc-700">{payment.description}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-sm text-zinc-500">
                    <Calendar size={14} />
                    <span>Due since: {formatDate(payment.date)}</span>
                  </div>
                </div>
                <div className="text-lg font-bold text-orange-700">
                  {formatCurrency(payment.amount)}
                </div>
              </div>
            ))}
            <div className="px-6 py-3 bg-orange-100/30 flex justify-end items-center gap-2 border-t border-orange-200/50">
              <span className="text-sm font-medium text-orange-800">Total Pending:</span>
              <span className="text-xl font-black text-orange-700">
                {formatCurrency(pendingPayments.reduce((sum, p) => sum + p.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Spent</p>
            <DollarSign className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{formatCurrency(stats.totalSpent)}</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Total Visits</p>
            <CheckCircle className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{stats.totalAttendance}</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">This Month</p>
            <TrendingUp className="text-lime-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{stats.monthlyAttendance}</p>
          <p className="text-xs text-zinc-500 mt-1">visits</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-zinc-500 uppercase tracking-wider">Subscriptions</p>
            <CreditCard className="text-purple-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-zinc-900">{subscriptions.length}</p>
          <p className="text-xs text-zinc-500 mt-1">total</p>
        </div>
      </div>



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscriptions */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Membership Subscriptions
            </h2>
          </div>
          <div className="overflow-y-auto max-h-96">
            {subscriptions.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No subscriptions yet
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {subscriptions.map((sub) => {
                  const statusInfo = getSubscriptionStatus(sub.end_date, sub.status);
                  const StatusIcon = statusInfo.icon;
                  return (
                    <div key={sub.id} className="px-6 py-4 hover:bg-zinc-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-bold text-zinc-900">{getPlanName(sub.plan_id)}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <StatusIcon size={14} className={statusInfo.color} />
                            <span className={`text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 text-sm text-zinc-600 mt-3">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            Start:
                          </span>
                          <span className="font-medium">{formatDate(sub.start_date)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            End:
                          </span>
                          <span className="font-medium">{formatDate(sub.end_date)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Attendance History */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Attendance History
            </h2>
          </div>
          <div className="overflow-y-auto max-h-96">
            {attendance.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No attendance records
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {attendance.map((record) => (
                  <div key={record.id} className="px-6 py-4 hover:bg-zinc-50 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2 text-zinc-900 font-medium mb-1">
                          <Calendar size={14} />
                          <span>{formatDate(record.date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <Clock size={12} />
                          <span>Check-in: {new Date(record.check_in_time).toLocaleTimeString()}</span>
                        </div>
                        {record.check_out_time && (
                          <div className="flex items-center gap-2 text-sm text-zinc-600 mt-1">
                            <Clock size={12} />
                            <span>Check-out: {new Date(record.check_out_time).toLocaleTimeString()}</span>
                          </div>
                        )}
                      </div>
                      <CheckCircle size={20} className="text-lime-500" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm lg:col-span-2">
          <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Payment History
            </h2>
          </div>
          <div className="overflow-y-auto max-h-96">
            {payments.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No payment records
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {payments.map((payment) => (
                  <div key={payment.id} className="px-6 py-4 hover:bg-zinc-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-mono text-zinc-500 bg-zinc-100 px-2 py-1 rounded">
                            {payment.receipt_number}
                          </span>
                          <span className="text-xs text-zinc-600 capitalize px-2 py-1 bg-zinc-100 rounded">
                            {(payment.receipt_type || '').replace('_', ' ')}
                          </span>
                          <span className="text-xs text-zinc-500 capitalize px-2 py-1 bg-blue-50 text-blue-700 rounded">
                            {(payment.payment_method || '').replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                          <Calendar size={14} />
                          <span>{formatDate(payment.receipt_date)}</span>
                        </div>
                        {payment.description && (
                          <div className="text-xs text-zinc-500 mt-1">{payment.description}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                        <div className="flex items-center justify-end gap-2 mt-2">
                          <button onClick={() => openReceiptEdit(payment)} className="p-1 hover:bg-blue-50 rounded" title="Edit">
                            <Edit2 size={14} className="text-blue-600" />
                          </button>
                          <button onClick={() => deleteReceipt(payment)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 size={14} className="text-red-600" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden Print Profile */}
      <MemberPrintProfile
        ref={profileRef}
        member={member}
        subscriptions={subscriptions.map(sub => ({
          ...sub,
          plan_name: plans.find(p => p.id == sub.plan_id)?.name
        }))}
        company={JSON.parse(localStorage.getItem('user'))?.company}
      />

      {showAdmissionPrintDialog && printAdmissionReceipt && (
        <Dialog open={showAdmissionPrintDialog} onOpenChange={setShowAdmissionPrintDialog}>
          <DialogContent aria-describedby="admission-print-dialog">
            <DialogHeader>
              <DialogTitle>Print Admission Fee Receipt</DialogTitle>
            </DialogHeader>
            <p id="admission-print-dialog" className="text-sm text-zinc-600 mb-4">
              {printAdmissionReceipt.member_name} - {formatCurrency(printAdmissionReceipt.amount)}
            </p>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => handleAdmissionPrint('a4')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A4
                </Button>
                <Button onClick={() => handleAdmissionPrint('a5')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A5
                </Button>
              </div>
              <Button onClick={() => handleAdmissionPrint('thermal')} className="w-full bg-purple-500 hover:bg-purple-600">
                <Printer size={16} className="mr-2" />
                Print Thermal
              </Button>
              <div className="border-t pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleAdmissionDownloadPDF('a4')} variant="outline">
                    Download PDF
                  </Button>
                  <Button onClick={() => handleAdmissionSharePDF('a4')} variant="outline">
                    Share PDF
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {printAdmissionReceipt && (
        <ReceiptPrint ref={receiptRef} receipt={printAdmissionReceipt} receiptType="admission" />
      )}

      <Dialog open={isReceiptEditOpen} onOpenChange={setIsReceiptEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReceiptEdit} className="space-y-4">
            <div>
              <Label htmlFor="member_edit_amount">Amount</Label>
              <input
                id="member_edit_amount"
                type="number"
                step="0.01"
                value={receiptEditForm.amount}
                onChange={(e) => setReceiptEditForm({ ...receiptEditForm, amount: e.target.value })}
                className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                required
              />
            </div>
            <div>
              <Label htmlFor="member_edit_payment_method">Payment Method</Label>
              <Select
                value={receiptEditForm.payment_method}
                onValueChange={(value) => setReceiptEditForm({ ...receiptEditForm, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="member_edit_description">Description</Label>
              <input
                id="member_edit_description"
                value={receiptEditForm.description}
                onChange={(e) => setReceiptEditForm({ ...receiptEditForm, description: e.target.value })}
                className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
              />
            </div>
            <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">Update Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberDetail;
